import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import Pagination from '../../Pagination';
import PeakToolbar, { type BarAction } from '../../PeakToolbar';
import { fmtDateTime, money } from '../../../utils/region';
import {
  payrollSettingsApi, ACCOUNT_PROP, ANSWER_PROP, PRS_ITEM_ACCOUNT_CLASSES, PRS_PAY_DAYS, PRS_ROLES, PRS_SEED_ITEM_CODES,
  PrsAccount, PrsAccountField, PrsAccountTarget, PrsAnswerKey, PrsHistoryRow, PrsItem, PrsItemPage, PrsItemPatch, PrsItemRules,
  PrsProblem, PrsRole, PrsRoleRow, PrsScope, PrsSections, PrsSettings,
} from '../../../utils/api/payrollSettings';
import type { AwCtx } from '../ctx';
import { btn, btnPrimary, card, dmy, errBox, inp, note, num, pillAmber, pillGreen, pillGrey, sectionHead, table, td, th } from '../ui';
import { Dialog, Foot, Loader, actBtn, emptyLine, field, lbl } from '../dialog';
import { isReadOnlySeat } from './fixedAssets/shared';

// ── Payroll › Payroll settings (batch 10 R5 — PAY-14; design §2.4, §3.4.1, §4 R5) ──────────────────────
// Everything a payroll run will need answered BEFORE it is run, for one client, in eight sections:
//   1. the five core accounts (salary expense, employer SSO expense, salary / PND.1 / SSO payable), chosen
//      from this client's own chart or made here with "+ New account";
//   2. the income and deduction items — for each, is it taxable, in the social-security base, regular
//      income, and (deductions, employer costs) which accounts it books to; the client's own items too;
//   3. the welfare-fund switch, and 4. the usual pay day;
//   5–7 read-only: the employer's social-security registration, this month's SSO rate and where it comes
//      from, and the PND.1 withholding method with its "awaiting the accountant" chip;
//   8. (R6) read-only: the number the next payroll run of a month will take — the server's own numbering asked as a
//      dry run (GET /api/payroll/runs/next-no), a plain read that reserves nothing.
// ⛔ NOTHING IS PREFILLED (PAY-14, PQ14/PQ15): a new client reads 0 of 5 and every item unanswered, and only a
// person's choice changes that. The screen never suggests an account by its code or its name; the lists it
// offers are the server's (`choices` — live, active, postable, not a control account, not cash where an
// asset is allowed, of the right class), and an answer that stopped being eligible since (the account was
// deactivated in the chart) is drawn amber with the server's rule, and is not counted as answered.
// ⛔ A single control saves when it changes — a select, an item's answer, the pay day, the switch — and the
// screen then reads the whole settings body again, so the lists never go stale under the next choice.
// Anything TYPED (a new account, a new item) goes through a full-screen Dialog with `dirty`: X, Esc, the
// backdrop and Cancel ask once (the shell's question, never one of this file's own).
// ⛔ Nothing is sent that the server is certain to refuse — a refused write pages the admins: a new account's
// Create waits for the code, the Thai name, a code this chart does not already use and, for a class the
// server says needs one, a cash-flow section; a new item's Create waits for a code of the server's pattern
// that is not a built-in code and not already this client's; the firm switch, every Remove and every picker
// appear only where the server's body allows them (canEditFirmDefaults, canRemove, choices, locked).
// ⛔ A read-only seat (books / auditor) gets no write control at all — never one shown disabled. The menu
// already hides this screen from a books login (menus.ts staffOnly); the rule holds here as well.
// ⛔ The pane never writes ?menu / ?sub: the no-chart CTA asks the shell (ctx.setNav), which guards the jump.
// It reads ?focus=role:<role> | item:<CODE> (R6's link from a run: "OT is not answered") and highlights that row.
// ⛔ Statutory names (ภ.ง.ด.1, สปส.1-01) are literals here, never inside a translation.
// ⛔ (R6 review) A refused write keeps its reason on the glass: the re-read that follows a refusal (so the control
// shows what the server holds) never clears it. The reason goes only when the next write starts, or with the
// client; a failure to READ the settings is a message of its own (loadErr), which the next good read clears.

type TF = (key: string, opts?: Record<string, unknown>) => string;
type Choices = PrsSettings['choices'];
const PND1 = 'ภ.ง.ด.1';
const SSO_101 = 'สปส.1-01';
const FOCUS = /^(role|item):[A-Za-z0-9_]+$/;
const focusOutline: React.CSSProperties = { outline: '2px solid var(--accent)', outlineOffset: -2, background: 'var(--accent-bg)' };
const amberRow: React.CSSProperties = { background: '#FFFBEB' };
const amberBox: React.CSSProperties = { border: '1px solid #F1C232', background: '#FFFBEB' };

/** The server's refusal as a sentence of this screen: every code the settings routes answer has its own words. */
const prsErr = (e: any, t: TF): string => {
  if (e?.readOnlySeat) return String(e.message || '');
  const p = e?.payload || {};
  const code = String(p.code || '');
  const k = `prs_err_${code}`;
  // account_refused names the rule (`why`); the others ignore it.
  if (code && t(k) !== k) return t(k, { why: ruleText(t, p.rule) });
  return String(e?.message || '') || t('prs_err_generic');
};
const ruleText = (t: TF, rule: any): string => {
  const k = `prs_rule_${String(rule || '')}`;
  return t(k) !== k ? t(k) : t('prs_rule_other');
};
/** Which of the server's three choice lists serves an account of these classes. */
const choiceOf = (classes: readonly string[] | undefined): keyof Choices | null => {
  const k = [...(classes || [])].sort().join(',');
  return k === 'expense' ? 'expense' : k === 'liability' ? 'liability' : k === 'asset,liability' ? 'deduction' : null;
};
const itemClasses = (kind: string, f: PrsAccountField): readonly string[] => (PRS_ITEM_ACCOUNT_CLASSES as any)[kind]?.[f] || [];
const payDayLabel = (t: TF, d: string): string => (d === '' ? t('prs_pay_day_none') : d === 'eom' ? t('prs_pay_day_eom') : t('prs_pay_day_n', { day: d }));
const whoWhen = (name: string, at: number): string => (!name ? '—' : at ? `${name} · ${fmtDateTime(at)}` : name);
/** The choices for an account of these classes — none when the server's classes match no list (never a wrong list:
 *  an account of another class would be refused). */
const choicesFor = (s: PrsSettings, classes: readonly string[] | undefined): PrsAccount[] => { const k = choiceOf(classes); return (k && s.choices[k]) || []; };
function useAcctName() {
  const { i18n } = useTranslation();
  const thai = i18n.language === 'th';
  return (a: PrsAccount | null | undefined): string => (a ? `${a.code} · ${thai ? a.name_th || a.name_en : a.name_en || a.name_th}` : '');
}
/** A built-in item is named in the reader's language (prs_item_<CODE>); a client's own item by its stored
 *  names — Thai for a Thai reader, otherwise English. */
function useItemName() {
  const { t, i18n } = useTranslation();
  const thai = i18n.language === 'th';
  return (r: { code: string; builtIn?: boolean; nameEn?: string; nameTh?: string }): string =>
    (r.builtIn && (PRS_SEED_ITEM_CODES as readonly string[]).includes(r.code) ? t(`prs_item_${r.code}`) : (thai ? r.nameTh || r.nameEn : r.nameEn || r.nameTh) || r.code);
}
/** Scroll the deep-linked row into view once it is on the glass. */
function useScrollTo(focusKey: string, ready: boolean) {
  useEffect(() => {
    if (!focusKey || !ready) return;
    const el = document.querySelector(`[data-focus-key="${focusKey}"]`) as HTMLElement | null;
    el?.scrollIntoView?.({ block: 'center' });
  }, [focusKey, ready]);
}

// ── The pane ─────────────────────────────────────────────────────────────────────────────────────────
export function PayrollSettingsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const focusKey = FOCUS.test(params.get('focus') || '') ? String(params.get('focus')) : '';
  const readOnly = isReadOnlySeat(ctx);
  const [s, setS] = useState<PrsSettings | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [err, setErr] = useState('');
  const [reload, setReload] = useState(0);
  const [scope, setScope] = useState<PrsScope>('client');
  const [busyKey, setBusyKey] = useState('');
  const [history, setHistory] = useState(false);
  const [newAcct, setNewAcct] = useState<{ target: PrsAccountTarget; cls: string; title: string } | null>(null);
  useEffect(() => { setErr(''); }, [ctx.clientId]);
  useEffect(() => {
    let alive = true;
    payrollSettingsApi.get(ctx.clientId).then(d => { if (alive) { setS(d); setLoadErr(''); } }).catch((e: any) => { if (alive) setLoadErr(prsErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => setReload(n => n + 1);
  /** One single-control write: the control spins while it runs, the screen re-reads after it. */
  const write = async (key: string, fn: () => Promise<unknown>) => {
    if (busyKey) return;
    setBusyKey(key); setErr('');
    try { await fn(); refresh(); } catch (e: any) { setErr(prsErr(e, t)); refresh(); } finally { setBusyKey(''); }
  };
  const roleTitle = (r: PrsRole) => t(`prs_role_${r}`, { form: PND1 });

  if (!s) return loadErr ? <div style={errBox} data-settings-load-error>{loadErr}</div> : <Loader />;
  const firm = scope === 'firm';
  const actions: BarAction[] = [
    { key: 'history', label: t('prs_history'), icon: 'ti-history', title: t('prs_history_title'), onClick: () => setHistory(true) },
    // ⛔ Offered only when the server says this login may edit the firm default (an administrator).
    ...(s.canEditFirmDefaults && !readOnly ? [{
      key: 'scope', label: t(firm ? 'prs_scope_firm' : 'prs_scope_client'), icon: firm ? 'ti-building' : 'ti-user', tone: firm ? 'amber' as const : undefined,
      menu: [
        { key: 'client', label: t('prs_scope_client'), hint: t('prs_scope_client_hint'), icon: 'ti-user', onClick: () => setScope('client') },
        { key: 'firm', label: t('prs_scope_firm'), hint: t('prs_scope_firm_hint'), icon: 'ti-building', onClick: () => setScope('firm') },
      ],
    }] : []),
  ];
  const filters = (
    <>
      <span style={s.hasChart && s.accounts.answered >= s.accounts.total ? pillGreen : pillAmber} data-ready-chip data-answered={s.accounts.answered}>
        {t('prs_chip_accounts', { n: s.accounts.answered, total: s.accounts.total })}
      </span>
      <span style={s.items.unanswered ? pillAmber : pillGreen} data-items-chip data-count={s.items.unanswered}>
        {s.items.unanswered ? t('prs_chip_items', { count: s.items.unanswered }) : t('prs_chip_items_done')}
      </span>
    </>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-pane="payroll-settings" data-scope={scope}>
      <div style={{ ...card, padding: 0, overflow: 'visible' }}>
        <PeakToolbar filters={filters} actions={actions} />
      </div>
      {loadErr && <div style={errBox} data-settings-load-error>{loadErr}</div>}
      {err && <div style={errBox} data-settings-error>{err}</div>}
      {firm && (
        <div style={{ ...card, ...amberBox, fontSize: 12.5, color: '#92400E', display: 'flex', gap: 8 }} data-scope-banner>
          <i className="ti ti-building" aria-hidden style={{ marginTop: 2 }} /><div>{t('prs_firm_banner')}</div>
        </div>
      )}
      {!firm && (
        <AccountsSection ctx={ctx} s={s} readOnly={readOnly} busyKey={busyKey} focusKey={focusKey} t={t}
          onPick={(role, id) => write(`role:${role}`, () => payrollSettingsApi.setRole(ctx.clientId, role, id))}
          onNew={(role, cls) => setNewAcct({ target: { role }, cls, title: roleTitle(role) })} />
      )}
      <ItemsSection key={scope} ctx={ctx} s={s} scope={scope} readOnly={readOnly} focusKey={firm ? '' : focusKey} outerReload={reload} onWrote={refresh} onError={setErr}
        onNewAccount={(target, cls, title) => setNewAcct({ target, cls, title })} />
      {!firm && (
        <>
          <section style={card} data-section="welfare">
            <div style={sectionHead}>{t('prs_sec_welfare')}</div>
            {readOnly ? (
              <div style={{ fontSize: 12.5 }} data-welfare-state>{t(s.general.welfareEnabled ? 'prs_welfare_on' : 'prs_welfare_off')}</div>
            ) : (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 600 }}>
                <input type="checkbox" checked={s.general.welfareEnabled} disabled={!!busyKey} data-welfare-switch
                  onChange={e => { const v = e.target.checked; write('welfare', () => payrollSettingsApi.setGeneral(ctx.clientId, { welfareEnabled: v })); }} />
                {t('prs_welfare_switch')}
              </label>
            )}
            <div style={{ ...note, marginTop: 6 }}>{t('prs_welfare_note')}</div>
          </section>
          <section style={card} data-section="defaults">
            <div style={sectionHead}>{t('prs_sec_defaults')}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <label htmlFor="prs-pay-day" style={{ ...lbl, marginBottom: 0 }}>{t('prs_pay_day')}</label>
              {readOnly ? <span style={{ fontSize: 12.5 }} data-pay-day-value>{payDayLabel(t, s.general.payDay)}</span> : (
                <select id="prs-pay-day" value={s.general.payDay} disabled={!!busyKey} style={{ ...inp, minWidth: 180 }} data-pay-day
                  onChange={e => { const v = e.target.value; write('pay_day', () => payrollSettingsApi.setGeneral(ctx.clientId, { payDay: v })); }}>
                  {PRS_PAY_DAYS.map(d => <option key={d || 'none'} value={d}>{payDayLabel(t, d)}</option>)}
                </select>
              )}
            </div>
            <div style={{ ...note, marginTop: 6 }}>{t('prs_pay_day_hint')}</div>
            {s.general.updatedByName && <div style={{ ...note, marginTop: 2 }} data-general-who>{t('prs_last_changed', { who: whoWhen(s.general.updatedByName, s.general.updatedAt) })}</div>}
          </section>
          <EmployerSsoSection s={s} t={t} />
          <SsoRateSection s={s} t={t} />
          <Pnd1Section s={s} t={t} />
          <NumberingSection ctx={ctx} today={s.today} t={t} />
        </>
      )}
      {newAcct && !readOnly && (
        <NewAccountDialog ctx={ctx} target={newAcct.target} cls={newAcct.cls} title={newAcct.title} codesInUse={s.codesInUse}
          cf={s.cfSections} fs={s.fsSections} onClose={() => setNewAcct(null)} onCreated={() => { setNewAcct(null); setErr(''); refresh(); }} />
      )}
      {history && <HistoryDialog ctx={ctx} s={s} onClose={() => setHistory(false)} />}
    </div>
  );
}

// ── 1. The five core accounts ───────────────────────────────────────────────────────────────────────
function AccountsSection({ ctx, s, readOnly, busyKey, focusKey, onPick, onNew, t }: {
  ctx: AwCtx; s: PrsSettings; readOnly: boolean; busyKey: string; focusKey: string; t: TF;
  onPick: (role: PrsRole, accountId: string) => void; onNew: (role: PrsRole, cls: string) => void;
}) {
  const acctName = useAcctName();
  useScrollTo(focusKey.startsWith('role:') ? focusKey : '', s.hasChart);
  const rows: PrsRoleRow[] = PRS_ROLES.map(r => s.accounts.rows.find(x => x.role === r)).filter(Boolean) as PrsRoleRow[];
  return (
    <section style={card} data-section="accounts">
      <div style={sectionHead}>{t('prs_sec_accounts')}</div>
      {!s.hasChart ? (
        // ⛔ Only this section offers the way out, and it is the shell's jump, not a write of its own:
        // Accounting › Periods opens the first period, which copies the template chart in (openPeriod).
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }} data-no-chart>
          <i className="ti ti-info-circle" aria-hidden style={{ color: '#92400E' }} />
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', flex: 1 }}>{t('prs_no_chart')}</span>
          <button style={btnPrimary} onClick={() => ctx.setNav('accounting', 'periods')} data-no-chart-cta>
            <i className="ti ti-calendar-plus" aria-hidden /> {t('prs_no_chart_cta')}
          </button>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 8, color: s.accounts.answered >= s.accounts.total ? '#166534' : '#92400E' }}
            data-accounts-ready data-answered={s.accounts.answered}>
            {t('prs_accounts_ready', { n: s.accounts.answered, total: s.accounts.total })}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={table}>
              <thead><tr><th style={th}>{t('prs_col_role')}</th><th style={th}>{t('prs_col_account')}</th><th style={th}>{t('prs_col_answered')}</th></tr></thead>
              <tbody>
                {rows.map(r => {
                  const key = `role:${r.role}`;
                  const opts = choicesFor(s, r.classes);
                  const off = !r.accountId || !!r.problem;
                  const cls = String(r.classes[0] || '');
                  return (
                    <tr key={r.role} data-role-row={r.role} data-focus-key={key} data-focused={focusKey === key ? '1' : undefined}
                      style={focusKey === key ? focusOutline : off ? amberRow : undefined}>
                      <td style={{ ...td, minWidth: 220 }}>
                        <div style={{ fontWeight: 700 }}>{t(`prs_role_${r.role}`, { form: PND1 })}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2 }}>{t(`prs_role_${r.role}_hint`, { form: PND1 })}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{r.classes.map(c => t(`pa_chart_class_${c}`)).join(' / ')}</div>
                      </td>
                      <td style={td}>
                        {readOnly ? (
                          <span data-role-value={r.role}>{r.account ? acctName(r.account) : t('prs_unanswered')}</span>
                        ) : (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            <select value={r.accountId} disabled={!!busyKey} data-role-select={r.role} aria-label={t(`prs_role_${r.role}`, { form: PND1 })}
                              onChange={e => onPick(r.role, e.target.value)} style={{ ...inp, minWidth: 260, maxWidth: 420, ...(off ? amberBox : {}) }}>
                              <option value="">{t('prs_unanswered')}</option>
                              {opts.map(a => <option key={a.id} value={a.id}>{acctName(a)}</option>)}
                              {r.accountId && !opts.some(a => a.id === r.accountId) && <option value={r.accountId}>{r.account ? acctName(r.account) : t('prs_account_unknown')}</option>}
                            </select>
                            {busyKey === key && <i className="ti ti-loader" aria-hidden />}
                            <button style={actBtn} onClick={() => onNew(r.role, cls)} disabled={!!busyKey} data-new-account={key} title={t('prs_new_account_title')}>
                              <i className="ti ti-plus" aria-hidden /> {t('prs_new_account')}
                            </button>
                          </div>
                        )}
                        {r.problem && <div style={{ fontSize: 11.5, color: '#92400E', marginTop: 4 }} data-role-problem={r.role} data-rule={r.problem.rule}>{ruleText(t, r.problem.rule)}</div>}
                        {!r.accountId && <div style={{ fontSize: 11.5, color: '#92400E', marginTop: 4 }} data-role-missing={r.role}>{t('prs_not_answered')}</div>}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{whoWhen(r.setByName, r.setAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ ...note, marginTop: 8 }}>{t('prs_accounts_note')}</div>
        </>
      )}
    </section>
  );
}

// ── 2. Income and deduction items ───────────────────────────────────────────────────────────────────
function ItemsSection({ ctx, s, scope, readOnly, focusKey, outerReload, onWrote, onError, onNewAccount }: {
  ctx: AwCtx; s: PrsSettings; scope: PrsScope; readOnly: boolean; focusKey: string; outerReload: number;
  onWrote: () => void; onError: (m: string) => void; onNewAccount: (target: PrsAccountTarget, cls: string, title: string) => void;
}) {
  const { t } = useTranslation();
  const acctName = useAcctName();
  const itemName = useItemName();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PrsItemPage | null>(null);
  const [err, setErr] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    let alive = true;
    payrollSettingsApi.items(ctx.clientId, scope, page).then(d => { if (alive) { setData(d); setErr(''); } }).catch((e: any) => { if (alive) setErr(prsErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, scope, page, outerReload]);   // eslint-disable-line react-hooks/exhaustive-deps
  useScrollTo(focusKey.startsWith('item:') ? focusKey : '', !!data);
  const firm = scope === 'firm';
  const answer = async (r: PrsItem, key: string, patch: PrsItemPatch) => {
    if (busyKey) return;
    setBusyKey(`${r.code}:${key}`); onError('');
    try {
      const { row } = await payrollSettingsApi.answerItem(ctx.clientId, r.code, { scope, ...patch });
      setData(d => (d ? { ...d, rows: d.rows.map(x => (x.code === r.code ? row : x)) } : d));
      onWrote();
    } catch (e: any) { onError(prsErr(e, t)); onWrote(); } finally { setBusyKey(''); }
  };
  const remove = async (r: PrsItem) => {
    if (busyKey || !window.confirm(t(r.builtIn ? 'prs_item_revert_confirm' : 'prs_item_remove_confirm', { name: itemName(r) }))) return;
    setBusyKey(`${r.code}:remove`); onError('');
    try { await payrollSettingsApi.removeItem(ctx.clientId, r.code); onWrote(); } catch (e: any) { onError(prsErr(e, t)); onWrote(); } finally { setBusyKey(''); }
  };
  if (!data) return <section style={card} data-section="items"><div style={sectionHead}>{t('prs_sec_items')}</div>{err ? <div style={errBox}>{err}</div> : <Loader />}</section>;
  const rules = data.rules;
  const answerCols: PrsAnswerKey[] = Array.from(new Set(Object.values(rules.answerKeysByKind || {}).flat())) as PrsAnswerKey[];
  // Item accounts are the client's: the firm default never holds one (firm_no_accounts), and with no chart
  // there is nothing to choose from — nor anything "+ New account" could be added to (no_chart).
  const showAccounts = !firm;
  // Every code this client already reads (its own items and the firm's), not only this page's 100: a code on
  // another page would draw the 409 a refused POST pages the admins with.
  const codesTaken = new Set([...(rules.itemCodesInUse || []), ...data.rows.map(r => r.code)]);
  const answerSelect = (r: PrsItem, k: PrsAnswerKey) => {
    const v = r[ANSWER_PROP[k]];
    const miss = r.missing.includes(k);
    if (readOnly) return <span data-answer-value={k}>{v == null ? '—' : t(v ? 'prs_yes' : 'prs_no')}</span>;
    return (
      <select value={v == null ? '' : String(v)} disabled={r.locked || !!busyKey} data-answer={k} aria-label={t(`prs_answer_${k}`, { form: PND1 })}
        onChange={e => answer(r, k, { [ANSWER_PROP[k]]: e.target.value === '' ? null : Number(e.target.value) } as PrsItemPatch)}
        style={{ ...inp, ...(miss ? amberBox : {}) }}>
        <option value="">—</option>
        <option value="1">{t('prs_yes')}</option>
        <option value="0">{t('prs_no')}</option>
      </select>
    );
  };
  const accountCell = (r: PrsItem, f: PrsAccountField) => {
    const prop = ACCOUNT_PROP[f];
    const id = r[prop];
    const acct = f === 'account_id' ? r.account : r.counterAccount;
    const problem: PrsProblem = f === 'account_id' ? r.accountProblem : r.counterAccountProblem;
    const classes = itemClasses(r.kind, f);
    const opts = choicesFor(s, classes);
    const miss = r.missing.includes(f) || !!problem;
    const label = t(`prs_itemacct_${r.kind}_${f}`);
    return (
      <div key={f} style={{ marginBottom: 6 }} data-item-account-cell={f}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.03em' }}>{label}</div>
        {readOnly || !s.hasChart ? (
          <span style={{ fontSize: 12 }} data-item-account-value={f}>{acct ? acctName(acct) : t(s.hasChart ? 'prs_unanswered' : 'prs_items_no_chart')}</span>
        ) : (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <select value={id} disabled={!!busyKey} data-item-account={f} aria-label={label}
              onChange={e => answer(r, f, { [prop]: e.target.value } as PrsItemPatch)} style={{ ...inp, maxWidth: 260, ...(miss ? amberBox : {}) }}>
              <option value="">{t('prs_unanswered')}</option>
              {opts.map(a => <option key={a.id} value={a.id}>{acctName(a)}</option>)}
              {id && !opts.some(a => a.id === id) && <option value={id}>{acct ? acctName(acct) : t('prs_account_unknown')}</option>}
            </select>
            {!!classes.length && (
              <button style={{ ...actBtn, padding: '4px 7px' }} disabled={!!busyKey} title={t('prs_new_account_title')} aria-label={t('prs_new_account')}
                data-new-account={`item:${r.code}:${f}`} onClick={() => onNewAccount({ itemCode: r.code, field: f }, String(classes[0]), `${itemName(r)} — ${label}`)}>
                <i className="ti ti-plus" aria-hidden />
              </button>
            )}
          </div>
        )}
        {problem && <div style={{ fontSize: 11, color: '#92400E', marginTop: 2 }} data-item-problem={f} data-rule={problem.rule}>{ruleText(t, problem.rule)}</div>}
      </div>
    );
  };
  return (
    <section style={card} data-section="items">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={{ ...sectionHead, marginBottom: 0 }}>{t('prs_sec_items')}</div>
        <span style={data.unanswered ? pillAmber : pillGreen} data-items-unanswered data-count={data.unanswered}>
          {data.unanswered ? t('prs_items_unanswered', { count: data.unanswered }) : t('prs_items_all_answered')}
        </span>
        <div style={{ flex: 1 }} />
        {!firm && !readOnly && (
          <button style={btn} onClick={() => setCreating(true)} data-new-item><i className="ti ti-plus" aria-hidden /> {t('prs_new_item')}</button>
        )}
      </div>
      {err && <div style={{ ...errBox, marginBottom: 8 }}>{err}</div>}
      <div style={{ overflowX: 'auto' }}>
        {!data.rows.length ? <div style={emptyLine}>{t('prs_items_none')}</div> : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t('prs_col_item')}</th><th style={th}>{t('prs_col_kind')}</th>
                {answerCols.map(k => <th key={k} style={th}>{t(`prs_answer_${k}`, { form: PND1 })}</th>)}
                {showAccounts && <th style={th}>{t('prs_col_accounts')}</th>}
                <th style={th}>{t('prs_col_answered')}</th><th style={th} aria-hidden />
              </tr>
            </thead>
            <tbody>
              {data.rows.map(r => {
                const key = `item:${r.code}`;
                const keys = rules.answerKeysByKind?.[r.kind] || [];
                const fields = rules.accountFieldsByKind?.[r.kind] || [];
                const amber = r.missing.length > 0 || !!r.accountProblem || !!r.counterAccountProblem;
                return (
                  <tr key={r.id || r.code} data-item-row={r.code} data-focus-key={key} data-focused={focusKey === key ? '1' : undefined}
                    data-missing={r.missing.length} style={focusKey === key ? focusOutline : amber ? amberRow : undefined}>
                    <td style={{ ...td, minWidth: 180 }}>
                      <div style={{ fontWeight: 700 }}>{itemName(r)}</div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 3, alignItems: 'center' }}>
                        <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{r.code}</span>
                        {r.locked && <span style={pillGrey} data-chip="locked" title={t('prs_item_locked_hint')}>{t('prs_item_locked')}</span>}
                        {!r.builtIn && <span style={pillGrey} data-chip="custom">{t('prs_item_custom')}</span>}
                        {!firm && r.builtIn && !r.locked && (r.overridesFirm
                          ? <span style={pillGreen} data-chip="own" title={t('prs_item_own_hint')}>{t('prs_item_own')}</span>
                          : <span style={pillGrey} data-chip="firm" title={t('prs_item_from_firm_hint')}>{t('prs_item_from_firm')}</span>)}
                      </div>
                    </td>
                    <td style={{ ...td, fontSize: 12 }}>
                      <div>{t(`prs_kind_${r.kind}`)}</div>
                      <div style={{ color: 'var(--text-tertiary)' }}>{t(`prs_group_${r.summaryGroup}`)}</div>
                    </td>
                    {answerCols.map(k => <td key={k} style={td}>{keys.includes(k) ? answerSelect(r, k) : <span style={{ color: 'var(--text-tertiary)' }}>·</span>}</td>)}
                    {showAccounts && <td style={td}>{fields.length ? fields.map(f => accountCell(r, f)) : <span style={{ color: 'var(--text-tertiary)' }}>·</span>}</td>}
                    <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-secondary)', fontSize: 12 }}>{r.locked ? t('prs_answered_builtin') : whoWhen(r.answeredByName, r.answeredAt)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {busyKey.startsWith(`${r.code}:`) && <i className="ti ti-loader" aria-hidden />}
                      {r.canRemove && !readOnly && !firm && (
                        <button style={{ ...actBtn, color: '#B42318' }} disabled={!!busyKey} onClick={() => remove(r)} data-remove-item={r.code}>
                          <i className="ti ti-trash" aria-hidden /> {t(r.builtIn ? 'prs_item_revert' : 'prs_item_remove')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <Pagination page={page} total={data.total} limit={data.limit || 100} onPage={setPage} />
      <div style={{ ...note, marginTop: 6 }}>{t(firm ? 'prs_items_note_firm' : 'prs_items_note')}</div>
      {creating && !readOnly && !firm && (
        <NewItemDialog ctx={ctx} rules={rules} taken={codesTaken} onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); setPage(1); onError(''); onWrote(); }} />
      )}
    </section>
  );
}

// ── 5. The employer's social-security registration (read-only) ────────────────────────────────────────
// ⛔ No Edit here. The one editor (Classic view › the statutory books' employer social-security section,
// Bookkeeping/Headless.tsx) saves through registersExtra.saveEmployerSso, which records no updated_by and
// blanks the office, first-employee date and note whenever a caller leaves them out — a second door into it
// would be a second unaudited write. The counts are the server's, over the current employees with the one
// social-security rule (ssoCalc inSso / ssoIdFor): never "no SSO id typed", which counts every Thai employee.
function EmployerSsoSection({ s, t }: { s: PrsSettings; t: TF }) {
  const e = s.employerSso;
  const where = `${t('aw_classic')} › ${t('pa_headless_sec_sso')}`;
  const stat = (k: string, n: number, warn = false) => (
    <div style={{ minWidth: 150 }} data-sso-count={k}>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t(`prs_sso_${k}`)}</div>
      <div style={{ fontSize: 15, fontWeight: 800, color: warn && n ? '#92400E' : 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }} data-n={n}>{n}</div>
    </div>
  );
  return (
    <section style={card} data-section="employer_sso">
      <div style={sectionHead}>{t('prs_sec_employer_sso')}</div>
      {e.registered ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 8, fontSize: 12.5 }} data-employer-sso>
          <div><div style={lbl}>{t('prs_sso_account_no')}</div><span data-employer-sso-account style={{ fontVariantNumeric: 'tabular-nums' }}>{e.accountNo || '—'}</span></div>
          <div><div style={lbl}>{t('prs_sso_branch')}</div>{e.branchCode || '—'}</div>
          <div><div style={lbl}>{t('prs_sso_registered_on')}</div>{e.registeredOn ? dmy(e.registeredOn) : '—'}</div>
          <div><div style={lbl}>{SSO_101}</div>{e.form101FiledOn ? dmy(e.form101FiledOn) : '—'}</div>
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: '#92400E' }} data-employer-sso-none>{t('prs_sso_not_on_record')}</div>
      )}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 10 }}>
        {stat('current', e.current)}{stat('in', e.inSso)}{stat('in_no_number', e.inSsoWithoutNumber, true)}{stat('not_in', e.notInSso)}
      </div>
      <div style={{ fontSize: 12.5, marginTop: 8 }} data-sso-login={s.ssoLoginSet ? 'set' : 'unset'}>
        {t('prs_sso_login')}: <b>{t(s.ssoLoginSet ? 'prs_sso_login_set' : 'prs_sso_login_unset')}</b>
      </div>
      <div style={{ ...note, marginTop: 6 }}>{t('prs_sso_edit_where', { where })}</div>
    </section>
  );
}

// ── 6. This month's social-security rate (read-only) ──────────────────────────────────────────────────
function SsoRateSection({ s, t }: { s: PrsSettings; t: TF }) {
  const r = s.statutory.sso;
  const month = `${String(s.statutory.month).padStart(2, '0')}/${s.statutory.year}`;
  const src = `prs_sso_source_${r.source}`;
  return (
    <section style={card} data-section="sso_rate">
      <div style={sectionHead}>{t('prs_sec_sso_rate', { month })}</div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12.5 }} data-sso-rate data-rate={r.rate} data-cap={r.cap} data-floor={r.floor}>
        <div><div style={lbl}>{t('prs_rate_rate')}</div><b>{r.rate}%</b></div>
        <div><div style={lbl}>{t('prs_rate_cap')}</div><b>{money(r.cap)}</b></div>
        <div><div style={lbl}>{t('prs_rate_floor')}</div><b>{money(r.floor)}</b></div>
      </div>
      <div style={{ fontSize: 12, marginTop: 8, color: 'var(--text-secondary)' }} data-sso-source={r.source}>{t(src) !== src ? t(src) : String(r.source)}</div>
    </section>
  );
}

// ── 7. The PND.1 method (read-only) ──────────────────────────────────────────────────────────────────
function Pnd1Section({ s, t }: { s: PrsSettings; t: TF }) {
  const { config: c, source } = s.statutory.pnd1;
  let from = 0;
  return (
    <section style={card} data-section="pnd1">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 7 }}>
        <div style={{ ...sectionHead, marginBottom: 0 }}>{PND1} · {t('prs_sec_pnd1', { year: s.statutory.year })}</div>
        <span style={pillAmber} data-pnd1-chip title={t('prs_pnd1_awaiting_hint')}>{t('prs_pnd1_awaiting')}</span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }} data-pnd1-source={source.kind}>
        {source.kind === 'year'
          ? t('prs_pnd1_source_year', { year: s.statutory.year, who: whoWhen(source.setByName, source.setAt) })
          : t('prs_pnd1_source_global')}
      </div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12.5, marginBottom: 10 }}>
        <div><div style={lbl}>{t('prs_pnd1_expense')}</div>{t('prs_pnd1_expense_v', { rate: c.expenseRate, cap: money(c.expenseCap) })}</div>
        <div><div style={lbl}>{t('prs_pnd1_sso_cap')}</div>{money(c.ssoCap)}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 14, alignItems: 'start' }}>
        <table style={table}>
          <thead><tr><th style={th}>{t('prs_pnd1_band')}</th><th style={{ ...th, textAlign: 'right' }}>{t('prs_pnd1_rate')}</th></tr></thead>
          <tbody>
            {c.brackets.map((b, i) => {
              const lo = from; from = b.upTo == null ? from : b.upTo;
              return (
                <tr key={i} data-pnd1-bracket={i}>
                  <td style={td}>{b.upTo == null ? t('prs_pnd1_band_above', { from: money(lo, 0) }) : t('prs_pnd1_band_range', { from: money(lo, 0), to: money(b.upTo, 0) })}</td>
                  <td style={num}>{b.rate}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <table style={table}>
          <thead><tr><th style={th}>{t('prs_pnd1_deductions')}</th><th style={{ ...th, textAlign: 'right' }}>{t('prs_pnd1_amount')}</th></tr></thead>
          <tbody>
            {c.deductions.map((d, i) => (
              <tr key={i} data-pnd1-deduction={i}><td style={td}>{d.name}</td><td style={num}>{money(d.amount)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ ...note, marginTop: 10 }} data-pnd1-method={s.statutory.method}>{t('prs_pnd1_method', { form: PND1 })}</div>
    </section>
  );
}

// ── 8. Numbering (read-only; batch 10 R6, R5 left it here) ──────────────────────────────────────────────
// The number the next payroll run of a month will take — PR-YYYYMM-NNNN, per client, never given twice: not after
// a void, not after a delete. It is the server's own numbering asked as a dry run (GET /runs/next-no): a plain
// read that takes no write lock and reserves nothing, so two people looking see the same number and the run that
// is created first takes it. ⛔ A GET, and nothing else, ever goes out from here. The month opens on the server's
// Bangkok month (GET /settings `today`) and lists last year to next year, written mm/yyyy.
function NumberingSection({ ctx, today, t }: { ctx: AwCtx; today: string; t: TF }) {
  const start = String(today || '').slice(0, 7);
  const [ym, setYm] = useState(start);
  const [docNo, setDocNo] = useState('');
  const [err, setErr] = useState('');
  const y0 = Number(start.slice(0, 4)) || new Date().getFullYear();
  const months = [y0 - 1, y0, y0 + 1].flatMap(y => Array.from({ length: 12 }, (_, i) => `${y}-${String(i + 1).padStart(2, '0')}`));
  const mmYyyy = (v: string) => `${v.slice(5, 7)}/${v.slice(0, 4)}`;
  useEffect(() => {
    if (!/^\d{4}-\d{2}$/.test(ym)) return;
    let alive = true;
    setDocNo(''); setErr('');
    payrollSettingsApi.nextRunNo(ctx.clientId, ym).then(d => { if (alive) setDocNo(String(d?.docNo || '')); }).catch((e: any) => { if (alive) setErr(prsErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, ym]);   // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <section style={card} data-section="numbering">
      <div style={sectionHead}>{t('prs_sec_numbering')}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12.5 }}>
        <label htmlFor="prs-num-month" style={{ ...lbl, marginBottom: 0 }}>{t('prs_num_month')}</label>
        <select id="prs-num-month" value={ym} onChange={e => setYm(e.target.value)} style={{ ...inp, minWidth: 120 }} data-num-month>
          {months.map(m => <option key={m} value={m}>{mmYyyy(m)}</option>)}
        </select>
        <span>{t('prs_num_next', { month: mmYyyy(ym) })}</span>
        {err ? <span style={{ color: '#B42318' }} data-next-no-error>{err}</span>
          : docNo ? <b style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '.02em' }} data-next-no={docNo}>{docNo}</b>
            : <i className="ti ti-loader" aria-hidden />}
      </div>
      <div style={{ ...note, marginTop: 6 }}>{t('prs_num_note')}</div>
    </section>
  );
}

// ── "+ New account" (full screen, a form) ─────────────────────────────────────────────────────────────
// The class is the target's and is shown locked; the code and the names are the person's — nothing is
// suggested or prefilled (§3.4.1). One POST makes the chart row through accountingSetup.addAccount (logged in
// coa_change_log under this user) and gives the answer it was made for, in one transaction.
function NewAccountDialog({ ctx, target, cls, title, codesInUse, cf, fs, onClose, onCreated }: {
  ctx: AwCtx; target: PrsAccountTarget; cls: string; title: string; codesInUse: string[]; cf: PrsSections; fs: PrsSections;
  onClose: () => void; onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [nameTh, setNameTh] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [cfSection, setCfSection] = useState('');
  const [fsSection, setFsSection] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  const taken = useMemo(() => new Set((codesInUse || []).map(c => String(c).trim())), [codesInUse]);
  const cfNeeded = (cf?.requiredFor || []).includes(cls);
  const fsAllowed = (fs?.allowedFor || []).includes(cls);
  // ⛔ 'cash' is offered for an asset only: accountingSetup.validateAccount refuses it on any other class.
  const cfValues = (cf?.values || []).filter(v => v !== 'cash' || cls === 'asset');
  const codeTaken = !!code.trim() && taken.has(code.trim());
  const ready = !!code.trim() && !!nameTh.trim() && !codeTaken && (!cfNeeded || !!cfSection);
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try {
      await payrollSettingsApi.newAccount(ctx.clientId, target, {
        code: code.trim(), nameTh: nameTh.trim(), ...(nameEn.trim() ? { nameEn: nameEn.trim() } : {}),
        ...(cfSection ? { cfSection } : {}), ...(fsAllowed && fsSection ? { fsSection } : {}),
      });
      setDirty(false); onCreated();
    } catch (e: any) { setErr(prsErr(e, t)); } finally { setBusy(false); }
  };
  return (
    <Dialog fullscreen title={t('prs_na_title')} docNo={title} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-plus" label={t('prs_na_create')} t={t} />}>
      <div style={{ maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 12 }} data-new-account-form>
        <div style={note}>{t('prs_na_note')}</div>
        {err && <div style={errBox} data-na-error>{err}</div>}
        <div>
          <label style={lbl}>{t('prs_na_class')}</label>
          <input value={t(`pa_chart_class_${cls}`)} readOnly tabIndex={-1} style={{ ...field, color: 'var(--text-secondary)', background: 'var(--bg-app)' }} data-na="class" data-class={cls} />
        </div>
        <div>
          <label htmlFor="prs-na-code" style={lbl}>{t('pa_chart_ph_code')}</label>
          <input id="prs-na-code" value={code} onChange={e => ed(setCode, e.target.value)} style={{ ...field, ...(codeTaken ? amberBox : {}) }} data-na="code" />
          {codeTaken && <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 3 }} data-na-taken>{t('prs_na_code_taken', { code: code.trim() })}</div>}
        </div>
        <div>
          <label htmlFor="prs-na-th" style={lbl}>{t('pa_chart_ph_name_th')}</label>
          <input id="prs-na-th" value={nameTh} onChange={e => ed(setNameTh, e.target.value)} style={field} data-na="name_th" />
          <div style={{ ...note, marginTop: 3 }}>{t('pa_chart_name_th_manual')}</div>
        </div>
        <div>
          <label htmlFor="prs-na-en" style={lbl}>{t('pa_chart_ph_name_en')}</label>
          <input id="prs-na-en" value={nameEn} onChange={e => ed(setNameEn, e.target.value)} style={field} data-na="name_en" />
        </div>
        <div>
          <label htmlFor="prs-na-cf" style={lbl}>{t('prs_na_cf')}{cfNeeded && <span aria-hidden style={{ color: '#B42318', marginLeft: 3 }}>*</span>}</label>
          <select id="prs-na-cf" value={cfSection} onChange={e => ed(setCfSection, e.target.value)} style={{ ...field, ...(cfNeeded && !cfSection ? amberBox : {}) }} data-na="cf">
            <option value="">{t('pa_chart_cf_none')}</option>
            {cfValues.map(v => <option key={v} value={v}>{t('pa_chart_cf_prefixed', { section: t(`pa_chart_cf_${v}`) })}</option>)}
          </select>
          {cfNeeded && <div style={{ ...note, marginTop: 3 }}>{t('prs_na_cf_needed')}</div>}
        </div>
        {fsAllowed && (
          <div>
            <label htmlFor="prs-na-fs" style={lbl}>{t('prs_na_fs')}</label>
            <select id="prs-na-fs" value={fsSection} onChange={e => ed(setFsSection, e.target.value)} style={field} data-na="fs">
              <option value="">{t('pa_chart_fs_none')}</option>
              {(fs?.values || []).map(v => <option key={v} value={v}>{t('pa_chart_fs_prefixed', { section: t(`pa_chart_fs_${v}`) })}</option>)}
            </select>
          </div>
        )}
      </div>
    </Dialog>
  );
}

// ── A client's own item (full screen, a form) ─────────────────────────────────────────────────────────
// Kinds, the groups each kind may land on and the code's pattern are the server's (`rules`). A built-in code
// and a code this client already has are refused by the server (409), so Create stays off for them.
function NewItemDialog({ ctx, rules, taken, onClose, onCreated }: {
  ctx: AwCtx; rules: PrsItemRules; taken: Set<string>; onClose: () => void; onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [kind, setKind] = useState('');
  const [group, setGroup] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [nameTh, setNameTh] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  const pattern = useMemo(() => { try { return new RegExp(rules.codePattern); } catch { return null; } }, [rules.codePattern]);
  // ⛔ A client's own item never lands on the base-salary line (createItemType refuses group 'basic').
  const groups = (rules.groupsByKind?.[kind] || []).filter(g => g !== 'basic');
  const c = code.trim();
  const codeProblem = !c ? '' : (PRS_SEED_ITEM_CODES as readonly string[]).includes(c) ? 'seed' : taken.has(c) ? 'taken' : pattern && !pattern.test(c) ? 'pattern' : '';
  const ready = !!c && !!pattern && !codeProblem && !!kind && groups.includes(group) && !!nameEn.trim() && !!nameTh.trim();
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try {
      await payrollSettingsApi.createItem(ctx.clientId, { code: c, kind, summaryGroup: group, nameEn: nameEn.trim(), nameTh: nameTh.trim() });
      setDirty(false); onCreated();
    } catch (e: any) { setErr(prsErr(e, t)); } finally { setBusy(false); }
  };
  return (
    <Dialog fullscreen title={t('prs_ni_title')} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-plus" label={t('prs_ni_create')} t={t} />}>
      <div style={{ maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 12 }} data-new-item-form>
        <div style={note}>{t('prs_ni_note')}</div>
        {err && <div style={errBox} data-ni-error>{err}</div>}
        <div>
          <label htmlFor="prs-ni-code" style={lbl}>{t('prs_ni_code')}</label>
          <input id="prs-ni-code" value={code} onChange={e => ed(setCode, e.target.value.toUpperCase())} style={{ ...field, ...(codeProblem ? amberBox : {}) }} data-ni="code" />
          <div style={{ fontSize: 11.5, marginTop: 3, color: codeProblem ? '#B42318' : 'var(--text-tertiary)' }} data-ni-code-problem={codeProblem || undefined}>
            {codeProblem ? t(`prs_ni_code_${codeProblem}`) : t('prs_ni_code_hint')}
          </div>
        </div>
        <div>
          <label htmlFor="prs-ni-kind" style={lbl}>{t('prs_ni_kind')}</label>
          <select id="prs-ni-kind" value={kind} onChange={e => { ed(setKind, e.target.value); setGroup(''); }} style={field} data-ni="kind">
            <option value="">{t('prs_choose')}</option>
            {(rules.kinds || []).map(k => <option key={k} value={k}>{t(`prs_kind_${k}`)}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="prs-ni-group" style={lbl}>{t('prs_ni_group')}</label>
          <select id="prs-ni-group" value={group} onChange={e => ed(setGroup, e.target.value)} disabled={!kind} style={field} data-ni="group">
            <option value="">{t('prs_choose')}</option>
            {groups.map(g => <option key={g} value={g}>{t(`prs_group_${g}`)}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="prs-ni-en" style={lbl}>{t('prs_ni_name_en')}</label>
          <input id="prs-ni-en" value={nameEn} onChange={e => ed(setNameEn, e.target.value)} style={field} data-ni="name_en" />
        </div>
        <div>
          <label htmlFor="prs-ni-th" style={lbl}>{t('prs_ni_name_th')}</label>
          <input id="prs-ni-th" value={nameTh} onChange={e => ed(setNameTh, e.target.value)} style={field} data-ni="name_th" />
        </div>
        <div style={note}>{t('prs_ni_after')}</div>
      </div>
    </Dialog>
  );
}

// ── History (full screen, read-only, 100 a page) ──────────────────────────────────────────────────────
// This client's settings events and the firm default's item events, newest first (payroll_events). A value
// is shown as a person reads it — Yes / No, an account by its code, a pay day in words — never a raw id.
function HistoryDialog({ ctx, s, onClose }: { ctx: AwCtx; s: PrsSettings; onClose: () => void }) {
  const { t } = useTranslation();
  const itemName = useItemName();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ rows: PrsHistoryRow[]; total: number; page: number; limit: number } | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    payrollSettingsApi.history(ctx.clientId, page).then(d => { if (alive) setData(d); }).catch((e: any) => { if (alive) setErr(prsErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, page]);   // eslint-disable-line react-hooks/exhaustive-deps
  const codes = useMemo(() => {
    const m = new Map<string, string>();
    for (const list of [s.choices.expense, s.choices.liability, s.choices.deduction]) for (const a of list || []) m.set(a.id, a.code);
    for (const r of s.accounts.rows) if (r.account) m.set(r.account.id, r.account.code);
    return m;
  }, [s]);
  const has = (k: string) => t(k) !== k;
  const snake = (f: string) => String(f || '').replace(/[A-Z]/g, x => `_${x.toLowerCase()}`);
  const subject = (r: PrsHistoryRow): string => {
    const kind = has(`prs_ev_entity_${r.entity}`) ? t(`prs_ev_entity_${r.entity}`) : t('prs_ev_entity_other');
    if (r.entity === 'account_map' && (PRS_ROLES as readonly string[]).includes(r.entityId)) return `${kind} · ${t(`prs_role_${r.entityId}`, { form: PND1 })}`;
    if (r.entity === 'item_type') {
      const code = /^pit-firm-(.+)$/.exec(r.entityId)?.[1] || (/^[A-Z][A-Z0-9_]*$/.test(r.entityId) ? r.entityId : '');
      return code ? `${kind} · ${itemName({ code, builtIn: true, nameEn: code })}` : kind;
    }
    return kind;
  };
  const value = (f: string, v: any): string => {
    const k = snake(f);
    if (v === null || v === undefined || v === '') return k === 'pay_day' ? payDayLabel(t, '') : t('prs_hist_blank');
    if (['pnd1_taxable', 'sso_base', 'pnd1_regular', 'welfare_enabled', 'active', 'built_in'].includes(k)) return Number(v) ? t('prs_yes') : t('prs_no');
    if (k === 'account_id' || k === 'counter_account_id') return codes.get(String(v)) || t('prs_hist_account_other');
    if (k === 'pay_day') return payDayLabel(t, String(v));
    if (k === 'kind' && has(`prs_kind_${v}`)) return t(`prs_kind_${v}`);
    if (k === 'summary_group' && has(`prs_group_${v}`)) return t(`prs_group_${v}`);
    return String(v);
  };
  const footer = data ? <Pagination page={page} total={data.total} limit={data.limit} onPage={setPage} /> : null;
  return (
    <Dialog fullscreen title={t('prs_history')} onClose={onClose} footer={footer || <span />}>
      {err ? <div style={errBox}>{err}</div> : !data ? <Loader /> : !data.rows.length ? <div style={emptyLine}>{t('prs_hist_none')}</div> : (
        <table style={table} data-settings-history>
          <thead><tr><th style={th}>{t('prs_hist_when')}</th><th style={th}>{t('prs_hist_change')}</th><th style={th}>{t('prs_hist_who')}</th></tr></thead>
          <tbody>
            {data.rows.map(r => {
              const f = snake(r.field);
              const act = has(`prs_ev_action_${r.action}`) ? t(`prs_ev_action_${r.action}`) : t('prs_ev_action_other');
              return (
                <tr key={r.id} data-hist-id={r.id} data-hist-entity={r.entity} data-hist-field={f}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDateTime(r.at)}</td>
                  <td style={td}>
                    <b>{subject(r)}</b> · {act}{r.clientId === '' && <span style={{ ...pillGrey, marginLeft: 6 }}>{t('prs_scope_firm')}</span>}
                    {f && has(`prs_field_${f}`) && (
                      <div style={{ fontSize: 12, marginTop: 2 }}>
                        {t(`prs_field_${f}`, { form: PND1 })}: <span style={{ color: 'var(--text-tertiary)' }} data-old>{value(f, r.oldValue)}</span> → <span data-new>{value(f, r.newValue)}</span>
                      </div>
                    )}
                  </td>
                  <td style={td}>{r.actorName}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Dialog>
  );
}
